import { Box, HStack, IconButton, Text, useColorModeValue } from '@chakra-ui/react'
import { AlertTriangle, X } from 'lucide-react'
import React from 'react'

interface BannerProps {
  message: string
  /** Lucide icon component; defaults to a warning triangle. */
  icon?: React.ComponentType<{ size?: number; color?: string }>
  /** When provided, a close button is shown that invokes this handler. */
  onClose?: () => void
  /** Accessible label for the close button. */
  closeLabel?: string
}

/**
 * Compact, non-scrolling notice bar for the chat header. Presentation only;
 * callers own the condition and the message.
 *
 * On mobile the warning icon is dropped (too small to read) and the text reads
 * left-aligned like body copy; desktop keeps the centered icon-plus-text layout.
 */
export const Banner: React.FC<BannerProps> = ({ message, icon: Icon = AlertTriangle, onClose, closeLabel }) => {
  const bg = useColorModeValue('yellow.50', 'yellow.900')
  const borderColor = useColorModeValue('yellow.100', 'yellow.700')
  const textColor = useColorModeValue('yellow.800', 'yellow.200')

  return (
    <Box
      position='relative'
      pl={6}
      pr={onClose ? 12 : 6}
      py={2}
      bg={bg}
      borderBottom='1px solid'
      borderColor={borderColor}
    >
      <HStack spacing={2} align='center' justify={{ base: 'flex-start', md: 'center' }}>
        <Box as='span' display={{ base: 'none', md: 'inline-flex' }}>
          <Icon size={14} color='#B7791F' />
        </Box>
        <Text fontSize='xs' color={textColor} textAlign={{ base: 'left', md: 'center' }}>
          {message}
        </Text>
      </HStack>
      {onClose && (
        <IconButton
          aria-label={closeLabel ?? 'Close'}
          icon={<X size={16} />}
          onClick={onClose}
          variant='ghost'
          size='xs'
          color={textColor}
          position='absolute'
          right={2}
          top={{ base: 1, md: '50%' }}
          transform={{ base: 'none', md: 'translateY(-50%)' }}
        />
      )}
    </Box>
  )
}
